'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Save,
  Loader2,
  AlertCircle,
  Mic,
  ChevronDown,
  Check,
  Paperclip,
  Info,
  FileText,
  Sliders,
  Lock,
  ChevronRight,
  MessageSquare,
} from 'lucide-react';
import { useTranslation } from '@/providers/I18nProvider';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import Image from 'next/image';
import { getModelConfig } from '@/config/models';
import { useAgent } from '@/hooks/useAgent';
import { useToast } from '@/hooks/useToast';
import { useQueryClient } from '@tanstack/react-query';

interface ContentSettingsProps {
  agentId: string;
  agentName: string;
  initialSettings: {
    systemPrompt?: string;
    temperature?: number;
    maxTokens?: number;
    voiceInputEnabled?: boolean;
    voiceInputModel?: string;
    voiceEnabledPlatforms?: Array<{
      platformId: string;
      name: string;
      provider?: string; // Will be populated by parent or fetched
    }>;
    fileUploadEnabled?: boolean;
    maxFileSize?: number;
    allowedFileTypes?: string[];
    maxFilesPerMessage?: number;
    feedbackEnabled?: boolean;
  };
  availableModels?: Array<{
    llmProvider: string;
    llmPlatformId: string;
    model: string;
    modelLabel?: string;
  }>;
  hideVoiceSection?: boolean;
  hideFileUploadSection?: boolean;
}

interface VoiceModel {
  provider: string;
  model: string;
  label: string;
  platformId: string;
  platformName?: string;
}

const VOICE_MODELS = {
  OPENAI: [
    { model: 'whisper-1', label: 'Whisper' },
    { model: 'gpt-4o-transcribe', label: 'GPT-4o Transcribe' },
    { model: 'gpt-4o-mini-transcribe', label: 'GPT-4o Mini Transcribe' },
  ],
  GROQ: [
    { model: 'whisper-large-v3-turbo', label: 'Whisper Large v3 Turbo' },
    { model: 'whisper-large-v3', label: 'Whisper Large v3' },
  ],
};

export default function ContentSettings({ agentId, agentName, initialSettings, availableModels = [], hideVoiceSection = false, hideFileUploadSection = false }: ContentSettingsProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [systemPrompt, setSystemPrompt] = useState(initialSettings.systemPrompt || '');
  const [temperature, setTemperature] = useState(initialSettings.temperature ?? 0.7);
  const [maxTokens, setMaxTokens] = useState(initialSettings.maxTokens || 4096);
  const [voiceInputEnabled, setVoiceInputEnabled] = useState(initialSettings.voiceInputEnabled || false);
  const [voiceInputModel, setVoiceInputModel] = useState(initialSettings.voiceInputModel || '');
  const [fileUploadEnabled, setFileUploadEnabled] = useState(initialSettings.fileUploadEnabled || false);
  const [maxFileSize, setMaxFileSize] = useState(initialSettings.maxFileSize || 10);
  const [allowedFileTypes, setAllowedFileTypes] = useState<string[]>(
    initialSettings.allowedFileTypes || ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
  );
  const [maxFilesPerMessage, setMaxFilesPerMessage] = useState(initialSettings.maxFilesPerMessage || 5);
  const [feedbackEnabled, setFeedbackEnabled] = useState(initialSettings.feedbackEnabled ?? false);
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [isSavingVoice, setIsSavingVoice] = useState(false);
  const [isSavingFile, setIsSavingFile] = useState(false);
  const [isSavingFileToggle, setIsSavingFileToggle] = useState(false);
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Track if prompt/model config has changed
  const hasPromptChanges =
    systemPrompt !== (initialSettings.systemPrompt || '') ||
    temperature !== (initialSettings.temperature ?? 0.7) ||
    maxTokens !== (initialSettings.maxTokens || 4096);

  // Track if file upload settings have changed
  const hasFileUploadChanges =
    maxFileSize !== (initialSettings.maxFileSize || 10) ||
    JSON.stringify(allowedFileTypes.sort()) !==
      JSON.stringify((initialSettings.allowedFileTypes || ['image/jpeg', 'image/png', 'image/gif', 'image/webp']).sort()) ||
    maxFilesPerMessage !== (initialSettings.maxFilesPerMessage || 5);

  // Use feature flags hook
  const { chatFileUploadEnabled, chatVoiceModelsEnabled, chatFeedbackEnabled, isLoading: loadingFlags } = useFeatureFlags(agentId);

  // Use agent hook for mutation and to get organizationId
  const { updateAgent, agent, configDoc } = useAgent(agentId);
  const organizationId = configDoc?.organizationId;

  // Compute available voice models based on voice enabled platforms
  const availableVoiceModels = useMemo(() => {
    const voiceModels: VoiceModel[] = [];

    // Only add voice models if we have explicitly enabled voice platforms
    if (initialSettings.voiceEnabledPlatforms && initialSettings.voiceEnabledPlatforms.length > 0) {
      initialSettings.voiceEnabledPlatforms.forEach((platform) => {
        const provider = platform.provider;

        // Add voice models based on provider type
        if (provider === 'OPENAI' && VOICE_MODELS.OPENAI) {
          VOICE_MODELS.OPENAI.forEach((model) => {
            voiceModels.push({
              provider: 'OPENAI',
              model: model.model,
              label: model.label,
              platformId: platform.platformId,
              platformName: platform.name,
            });
          });
        } else if (provider === 'GROQ' && VOICE_MODELS.GROQ) {
          VOICE_MODELS.GROQ.forEach((model) => {
            voiceModels.push({
              provider: 'GROQ',
              model: model.model,
              label: model.label,
              platformId: platform.platformId,
              platformName: platform.name,
            });
          });
        }
      });
    }
    // No fallback - if no voice platforms are enabled, no voice models are available

    return voiceModels;
  }, [initialSettings.voiceEnabledPlatforms]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowModelDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSavePromptConfig = async () => {
    setError('');
    setSuccess(false);
    setIsSavingPrompt(true);

    try {
      await updateAgent.mutateAsync({
        systemPrompt,
        temperature,
        maxTokens,
      });

      setSuccess(true);
      showToast(t('admin.content.prompt.saveSuccess'), 'success');
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.content.errors.saveFailed'));
      showToast(err instanceof Error ? err.message : t('admin.content.errors.saveFailed'), 'error');
    } finally {
      setIsSavingPrompt(false);
    }
  };

  const handleSaveVoice = async (enabled?: boolean, model?: string) => {
    setError('');

    const voiceEnabled = enabled !== undefined ? enabled : voiceInputEnabled;
    const voiceModel = model !== undefined ? model : voiceInputModel;

    // Don't allow enabling voice if no voice providers are available
    if (voiceEnabled && !hasVoiceProviders) {
      setError(t('admin.content.voiceInput.noProvidersDescription'));
      showToast(t('admin.content.voiceInput.noProvidersDescription'), 'error');
      return;
    }

    // Validate that a model is selected if voice input is enabled
    if (voiceEnabled && !voiceModel) {
      setError(t('admin.content.voiceInput.errors.noModelSelected'));
      return;
    }

    setIsSavingVoice(true);

    try {
      await updateAgent.mutateAsync({
        voiceInputEnabled: chatVoiceModelsEnabled ? voiceEnabled : undefined,
        voiceInputModel: chatVoiceModelsEnabled && voiceEnabled ? voiceModel : undefined,
      });

      showToast(t('admin.content.voiceInput.saveSuccess'), 'success');

      // Invalidate cache to refresh agent data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.content.errors.saveFailed'));
      showToast(err instanceof Error ? err.message : t('admin.content.errors.saveFailed'), 'error');
    } finally {
      setIsSavingVoice(false);
    }
  };

  const handleSaveFileToggle = async (enabled: boolean) => {
    setError('');
    setIsSavingFileToggle(true);

    try {
      await updateAgent.mutateAsync({
        fileUploadEnabled: chatFileUploadEnabled ? enabled : undefined,
        maxFileSize: chatFileUploadEnabled && enabled ? maxFileSize : undefined,
        allowedFileTypes: chatFileUploadEnabled && enabled ? allowedFileTypes : undefined,
        maxFilesPerMessage: chatFileUploadEnabled && enabled ? maxFilesPerMessage : undefined,
      });

      showToast(t('admin.content.fileUpload.saveSuccess'), 'success');

      // Invalidate cache to refresh agent data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.content.errors.saveFailed'));
      showToast(err instanceof Error ? err.message : t('admin.content.errors.saveFailed'), 'error');
    } finally {
      setIsSavingFileToggle(false);
    }
  };

  const handleSaveFileSettings = async () => {
    setError('');
    setIsSavingFile(true);

    try {
      await updateAgent.mutateAsync({
        fileUploadEnabled: chatFileUploadEnabled ? fileUploadEnabled : undefined,
        maxFileSize: chatFileUploadEnabled && fileUploadEnabled ? maxFileSize : undefined,
        allowedFileTypes: chatFileUploadEnabled && fileUploadEnabled ? allowedFileTypes : undefined,
        maxFilesPerMessage: chatFileUploadEnabled && fileUploadEnabled ? maxFilesPerMessage : undefined,
      });

      showToast(t('admin.content.fileUpload.settingsSaved'), 'success');

      // Invalidate cache to refresh agent data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.content.errors.saveFailed'));
      showToast(err instanceof Error ? err.message : t('admin.content.errors.saveFailed'), 'error');
    } finally {
      setIsSavingFile(false);
    }
  };

  const handleSaveFeedback = async (enabled: boolean) => {
    setError('');
    setIsSavingFeedback(true);

    try {
      await updateAgent.mutateAsync({
        feedbackEnabled: chatFeedbackEnabled ? enabled : undefined,
      });

      showToast(t('admin.content.feedback.saveSuccess'), 'success');

      // Invalidate cache to refresh agent data
      await queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.content.errors.saveFailed'));
      showToast(err instanceof Error ? err.message : t('admin.content.errors.saveFailed'), 'error');
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const hasVoiceProviders = availableVoiceModels.length > 0;

  // Auto-disable voice input if it was enabled but there are no voice providers
  useEffect(() => {
    if (voiceInputEnabled && !hasVoiceProviders) {
      // Voice was enabled but no providers available - disable it
      setVoiceInputEnabled(false);
      setVoiceInputModel('');
      
      // Save the disabled state
      handleSaveVoice(false, '');
    }
  }, [hasVoiceProviders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if any available models support file upload
  const hasFileUploadModels = availableModels.some((model) => {
    const modelConfig = getModelConfig(model.llmProvider, model.model);
    return modelConfig?.supportsFileUpload;
  });

  const getSelectedModel = () => {
    if (!voiceInputModel) return null;
    return availableVoiceModels.find((m) => `${m.platformId}:${m.model}` === voiceInputModel);
  };

  const selectedModel = getSelectedModel();

  // Group models by platform (not provider) to handle multiple platforms of same type
  const modelsByPlatform = availableVoiceModels.reduce((acc, model) => {
    const key = `${model.platformId}:${model.provider}`;
    if (!acc[key]) {
      acc[key] = {
        platformId: model.platformId,
        platformName: model.platformName,
        provider: model.provider,
        models: [],
      };
    }
    acc[key].models.push(model);
    return acc;
  }, {} as Record<string, { platformId: string; platformName?: string; provider: string; models: VoiceModel[] }>);

  // Show skeleton loader while loading feature flags
  if (loadingFlags) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
            <div className="h-4 w-96 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>

          {/* Settings sections skeleton */}
          <div className="space-y-8">
            {/* System Prompt skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-4"></div>
              <div className="space-y-4">
                <div className="h-4 w-28 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                <div className="h-32 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
            </div>

            {/* Model Configuration skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-6"></div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
                  <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                </div>
                <div>
                  <div className="h-4 w-36 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2"></div>
                  <div className="h-10 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Voice Input skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-6"></div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                  <div className="h-6 w-11 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* File Upload skeleton */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                <div className="h-6 w-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
              </div>
              <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-6"></div>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                  <div className="h-6 w-11 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button skeleton */}
          <div className="mt-8 flex justify-end">
            <div className="h-10 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{t('admin.content.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('admin.content.description')}</p>
        </div>

        {/* Settings sections */}
        <div className="space-y-8">
          {/* Combined Prompt & Model Configuration Section */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.content.promptAndModel.title')}</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{t('admin.content.promptAndModel.description')}</p>

            <div className="space-y-6">
              {/* System Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('admin.content.systemPrompt.promptLabel')}
                </label>
                <textarea
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows={8}
                  placeholder={t('admin.content.systemPrompt.promptPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                           bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                           focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                           font-mono text-sm"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('admin.content.systemPrompt.promptHelp')}</p>
              </div>

              {/* Model Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Temperature */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.content.modelConfig.temperatureLabel')}
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={temperature}
                      onChange={(e) => setTemperature(parseFloat(e.target.value))}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                    />
                    <input
                      type="number"
                      min="0"
                      max="2"
                      step="0.1"
                      value={temperature}
                      onChange={(e) => setTemperature(parseFloat(e.target.value))}
                      className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-700 rounded 
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                               focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('admin.content.modelConfig.temperatureHelp')}</p>
                </div>

                {/* Max Tokens */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('admin.content.modelConfig.maxTokensLabel')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="128000"
                    value={maxTokens}
                    onChange={(e) => setMaxTokens(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                             focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('admin.content.modelConfig.maxTokensHelp')}</p>
                </div>
              </div>

              {/* Save Button for this section */}
              <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSavePromptConfig}
                  disabled={isSavingPrompt || !hasPromptChanges}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg
                           transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                           flex items-center gap-2"
                >
                  {isSavingPrompt ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      {t('admin.content.saveButton')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </section>

          {/* User Feedback Section */}
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 relative">
            {!chatFeedbackEnabled && <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 rounded-lg" />}

            <div className="flex items-center gap-2 mb-4 relative z-10">
              <MessageSquare className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.content.feedback.title')}</h2>
              {!chatFeedbackEnabled && (
                <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                  {t('admin.sidebar.badges.proEnterprise')}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 relative z-10">{t('admin.content.feedback.description')}</p>

            {!chatFeedbackEnabled && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg relative z-20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <MessageSquare className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('upgradeCTA.feedback.title')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('upgradeCTA.feedback.description')}</p>
                    <div className="flex items-center gap-3">
                      <a
                        href={`${process.env.NEXT_PUBLIC_VECTORIZE_BASE_URL || 'https://app.vectorize.io'}/organization/${organizationId}/admin/billing/changeplan`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                      >
                        {t('upgradeCTA.upgradeToPro')}
                        <ChevronRight className="h-4 w-4" />
                      </a>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{t('upgradeCTA.feedback.features')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Enable Feedback Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('admin.content.feedback.enableLabel')}</label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.content.feedback.enableHelp')}</p>
              </div>
              <button
                type="button"
                onClick={async () => {
                  if (chatFeedbackEnabled && !isSavingFeedback) {
                    const newEnabledState = !feedbackEnabled;
                    setFeedbackEnabled(newEnabledState);
                    await handleSaveFeedback(newEnabledState);
                  }
                }}
                disabled={!chatFeedbackEnabled || isSavingFeedback}
                className={`
                  relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                  ${feedbackEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}
                  ${!chatFeedbackEnabled || isSavingFeedback ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <span
                  className={`
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                    ${feedbackEnabled ? 'translate-x-6' : 'translate-x-1'}
                  `}
                />
              </button>
            </div>
          </section>

          {/* Voice Input Section */}
          {!hideVoiceSection && (
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 relative">
            {!chatVoiceModelsEnabled && <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 rounded-lg" />}

            <div className="flex items-center gap-2 mb-4 relative z-10">
              <Mic className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.content.voiceInput.title')}</h2>
              {!chatVoiceModelsEnabled && (
                <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                  {t('admin.sidebar.badges.proEnterprise')}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 relative z-10">{t('admin.content.voiceInput.description')}</p>

            {!chatVoiceModelsEnabled && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg relative z-20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Mic className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('upgradeCTA.voiceInput.title')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('upgradeCTA.voiceInput.description')}</p>
                    <div className="flex items-center gap-3">
                      <a
                        href={`${process.env.NEXT_PUBLIC_VECTORIZE_BASE_URL || 'https://app.vectorize.io'}/organization/${organizationId}/admin/billing/changeplan`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                      >
                        {t('upgradeCTA.upgradeToPro')}
                        <ChevronRight className="h-4 w-4" />
                      </a>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{t('upgradeCTA.voiceInput.features')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {hasVoiceProviders ? (
              <div className="space-y-4">
                {/* Enable Voice Input Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('admin.content.voiceInput.enableLabel')}</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.content.voiceInput.enableHelp')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!chatVoiceModelsEnabled) return;

                      const newEnabledState = !voiceInputEnabled;
                      setVoiceInputEnabled(newEnabledState);

                      if (newEnabledState) {
                        // Auto-select first available model when enabling
                        if (availableVoiceModels.length > 0) {
                          const firstModel = availableVoiceModels[0];
                          const modelId = `${firstModel.platformId}:${firstModel.model}`;
                          setVoiceInputModel(modelId);
                          // Auto-save with the new enabled state and model
                          await handleSaveVoice(true, modelId);
                        }
                      } else {
                        // Clear model when disabling
                        setVoiceInputModel('');
                        // Auto-save the disabled state
                        await handleSaveVoice(false, '');
                      }
                    }}
                    disabled={!chatVoiceModelsEnabled || isSavingVoice}
                    className={`
                      relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                      ${voiceInputEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}
                      ${!chatVoiceModelsEnabled || isSavingVoice ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <span
                      className={`
                        inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                        ${voiceInputEnabled ? 'translate-x-6' : 'translate-x-1'}
                      `}
                    />
                  </button>
                </div>

                {/* Voice Model Selection */}
                {voiceInputEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('admin.content.voiceInput.modelLabel')}
                    </label>

                    {/* Custom Dropdown */}
                    <div ref={dropdownRef} className="relative">
                      <button
                        type="button"
                        onClick={() => chatVoiceModelsEnabled && !isSavingVoice && setShowModelDropdown(!showModelDropdown)}
                        disabled={!chatVoiceModelsEnabled || isSavingVoice}
                        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                                 flex items-center justify-between
                                 ${isSavingVoice ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          {selectedModel ? (
                            <>
                              {selectedModel.provider === 'OPENAI' && (
                                <Image src="/logos/openai.svg" alt="OpenAI" width={16} height={16} className="dark:hidden" />
                              )}
                              {selectedModel.provider === 'OPENAI' && (
                                <Image src="/logos/openai-dm.svg" alt="OpenAI" width={16} height={16} className="hidden dark:block" />
                              )}
                              {selectedModel.provider === 'GROQ' && <Image src="/logos/groq-square.png" alt="Groq" width={16} height={16} />}
                              <span>{selectedModel.label}</span>
                            </>
                          ) : (
                            <span className="text-gray-500">Select a model</span>
                          )}
                        </div>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showModelDropdown ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Dropdown Menu */}
                      {showModelDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
                          {Object.values(modelsByPlatform).map((platform) => (
                            <div key={`${platform.platformId}:${platform.provider}`}>
                              {/* Platform Header */}
                              <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-2">
                                  {platform.provider === 'OPENAI' && (
                                    <>
                                      <Image src="/logos/openai.svg" alt="OpenAI" width={16} height={16} className="dark:hidden" />
                                      <Image src="/logos/openai-dm.svg" alt="OpenAI" width={16} height={16} className="hidden dark:block" />
                                    </>
                                  )}
                                  {platform.provider === 'GROQ' && <Image src="/logos/groq-square.png" alt="Groq" width={16} height={16} />}
                                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                    {platform.provider}
                                    {platform.platformName && ` - ${platform.platformName}`}
                                  </span>
                                </div>
                              </div>

                              {/* Models */}
                              {platform.models.map((model) => {
                                const isSelected = voiceInputModel === `${model.platformId}:${model.model}`;
                                return (
                                  <button
                                    key={`${model.platformId}:${model.model}`}
                                    type="button"
                                    onClick={async () => {
                                      const newModelId = `${model.platformId}:${model.model}`;
                                      // Only save if the model actually changed
                                      if (newModelId !== voiceInputModel) {
                                        setVoiceInputModel(newModelId);
                                        setShowModelDropdown(false);
                                        // Auto-save the new model selection
                                        await handleSaveVoice(voiceInputEnabled, newModelId);
                                      } else {
                                        setShowModelDropdown(false);
                                      }
                                    }}
                                    disabled={isSavingVoice}
                                    className={`
                                      w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 
                                      flex items-center justify-between transition-colors
                                      ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}
                                      ${isSavingVoice ? 'opacity-50 cursor-not-allowed' : ''}
                                    `}
                                  >
                                    <div className="flex items-center gap-2 flex-1">
                                      {model.provider === 'OPENAI' && (
                                        <>
                                          <Image src="/logos/openai.svg" alt="OpenAI" width={16} height={16} className="dark:hidden flex-shrink-0" />
                                          <Image
                                            src="/logos/openai-dm.svg"
                                            alt="OpenAI"
                                            width={16}
                                            height={16}
                                            className="hidden dark:block flex-shrink-0"
                                          />
                                        </>
                                      )}
                                      {model.provider === 'GROQ' && (
                                        <Image src="/logos/groq-square.png" alt="Groq" width={16} height={16} className="flex-shrink-0" />
                                      )}
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{model.label}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                          {t(`admin.content.voiceInput.models.${model.model.replace(/-/g, '_')}.description`)}
                                        </div>
                                      </div>
                                    </div>
                                    {isSelected && <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 ml-2" />}
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('admin.content.voiceInput.modelHelp')}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">{t('admin.content.voiceInput.noProvidersTitle')}</h3>
                <p className="text-xs text-yellow-700 dark:text-yellow-300">{t('admin.content.voiceInput.noProvidersDescription')}</p>
              </div>
            )}
          </section>
          )}

          {/* File Upload Section */}
          {!hideFileUploadSection && (
          <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 p-6 relative">
            {!chatFileUploadEnabled && <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 rounded-lg" />}

            <div className="flex items-center gap-2 mb-4 relative z-10">
              <Paperclip className="h-5 w-5 text-gray-400" />
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('admin.content.fileUpload.title')}</h2>
              {!chatFileUploadEnabled && (
                <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded">
                  {t('admin.sidebar.badges.proEnterprise')}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 relative z-10">{t('admin.content.fileUpload.description')}</p>

            {!chatFileUploadEnabled && (
              <div className="mb-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg relative z-20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <Paperclip className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{t('upgradeCTA.fileUpload.title')}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('upgradeCTA.fileUpload.description')}</p>
                    <div className="flex items-center gap-3">
                      <a
                        href={`${process.env.NEXT_PUBLIC_VECTORIZE_BASE_URL || 'https://app.vectorize.io'}/organization/${organizationId}/admin/billing/changeplan`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-sm"
                      >
                        {t('upgradeCTA.upgradeToPro')}
                        <ChevronRight className="h-4 w-4" />
                      </a>
                      <span className="text-sm text-gray-500 dark:text-gray-400">{t('upgradeCTA.fileUpload.features')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {hasFileUploadModels ? (
              <div className="space-y-6">
                {/* Enable File Upload Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('admin.content.fileUpload.enableLabel')}</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('admin.content.fileUpload.enableHelp')}</p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      if (chatFileUploadEnabled && !isSavingFileToggle) {
                        const newEnabledState = !fileUploadEnabled;
                        setFileUploadEnabled(newEnabledState);
                        await handleSaveFileToggle(newEnabledState);
                      }
                    }}
                    disabled={!chatFileUploadEnabled || isSavingFileToggle}
                    className={`
                      relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                      ${fileUploadEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}
                      ${!chatFileUploadEnabled || isSavingFileToggle ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <span
                      className={`
                        inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                        ${fileUploadEnabled ? 'translate-x-6' : 'translate-x-1'}
                      `}
                    />
                  </button>
                </div>

                {/* File Upload Settings */}
                {fileUploadEnabled && (
                  <div className="space-y-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700 ml-2">
                    {/* Max File Size */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.content.fileUpload.maxSizeLabel')}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="50"
                          value={maxFileSize}
                          onChange={(e) => chatFileUploadEnabled && setMaxFileSize(Number(e.target.value))}
                          disabled={!chatFileUploadEnabled}
                          className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-700 rounded 
                                   bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                   focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                                   disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">MB</span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('admin.content.fileUpload.maxSizeHelp')}</p>
                    </div>

                    {/* Max Files Per Message */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.content.fileUpload.maxFilesLabel')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={maxFilesPerMessage}
                        onChange={(e) => chatFileUploadEnabled && setMaxFilesPerMessage(Number(e.target.value))}
                        disabled={!chatFileUploadEnabled}
                        className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-700 rounded 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                                 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('admin.content.fileUpload.maxFilesHelp')}</p>
                    </div>

                    {/* Allowed File Types */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('admin.content.fileUpload.allowedTypesLabel')}
                      </label>
                      <div className="space-y-2">
                        {[
                          { value: 'image/jpeg', label: 'JPEG' },
                          { value: 'image/png', label: 'PNG' },
                          { value: 'image/gif', label: 'GIF' },
                          { value: 'image/webp', label: 'WebP' },
                        ].map((type) => (
                          <label key={type.value} className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={allowedFileTypes.includes(type.value)}
                              onChange={(e) => {
                                if (!chatFileUploadEnabled) return;
                                if (e.target.checked) {
                                  setAllowedFileTypes([...allowedFileTypes, type.value]);
                                } else {
                                  setAllowedFileTypes(allowedFileTypes.filter((t) => t !== type.value));
                                }
                              }}
                              disabled={!chatFileUploadEnabled}
                              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 
                                       focus:ring-blue-500 dark:focus:ring-blue-400
                                       disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">{type.label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('admin.content.fileUpload.allowedTypesHelp')}</p>
                    </div>

                    {/* Info about model support */}
                    <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-blue-700 dark:text-blue-300">{t('admin.content.fileUpload.modelSupportInfo')}</p>
                    </div>

                    {/* Save button for file upload settings */}
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleSaveFileSettings}
                        disabled={!hasFileUploadChanges || isSavingFile || !chatFileUploadEnabled}
                        className={`
                          px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                          focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400
                          flex items-center gap-2 transition-colors
                          ${!hasFileUploadChanges || isSavingFile || !chatFileUploadEnabled ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                      >
                        {isSavingFile ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t('admin.content.saving')}
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4" />
                            {t('admin.content.saveButton')}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">{t('admin.content.fileUpload.noModelsTitle')}</h3>
                <p className="text-xs text-yellow-700 dark:text-yellow-300">{t('admin.content.fileUpload.noModelsDescription')}</p>
              </div>
            )}
          </section>
          )}

          {/* Error/Success Messages */}
          {error && (
            <div className="flex items-start gap-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-700 dark:text-green-300">{t('admin.content.saveSuccess')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
